// ============================================================
// Newsletter Templates — MJML + editable body content
// ============================================================
//
// Each template has:
//   mjml           — full MJML document with {{BODY}} placeholder (header/footer locked)
//   defaultContent — HTML loaded into TipTap editor when template is selected
//   subject        — default subject line
//
// The editor only edits the body; header and footer are defined in MJML
// and cannot be modified by the user.
//
// getTemplates(tenant) injects tenant-specific branding into the MJML.

const MJ_HEAD = `
    <mj-head>
      <mj-attributes>
        <mj-all font-family="Calibri, Arial, Helvetica, sans-serif" />
        <mj-text font-size="15px" line-height="1.6" color="#333333" />
        <mj-section padding="0" />
      </mj-attributes>
      <mj-preview>{{PREHEADER}}</mj-preview>
      <mj-raw>
        <meta name="color-scheme" content="light dark" />
        <meta name="supported-color-schemes" content="light dark" />
      </mj-raw>
      <mj-style inline="inline">
        a { color: #335D95; }
        h1, h2, h3 { font-family: Georgia, 'Times New Roman', serif; color: #213D61; }
        h1 { font-size: 24px; font-weight: bold; }
        h2 { font-size: 21px; font-weight: bold; margin-top: 24px; }
        h3 { font-size: 18px; font-weight: bold; margin-top: 18px; }
      </mj-style>
    </mj-head>`;

function buildHeaderMjml(tenant) {
  const nl = tenant.newsletter;
  // Bulletproof button: VML roundrect for Outlook desktop, CSS for everything else.
  // mj-button alone uses CSS padding on <a> which Outlook ignores.
  const buttonMjml =
    nl.buttonLabel && nl.buttonMergeTag
      ? `<mj-raw>
            <div style="text-align:center;padding:8px 0 16px;">
              <!--[if mso]>
              <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word"
                href="${nl.buttonMergeTag}" style="height:38px;v-text-anchor:middle;width:180px;"
                arcsize="16%" stroke="f" fillcolor="${nl.buttonBg}">
                <w:anchorlock/>
                <center style="color:${nl.buttonText};font-family:Calibri,Arial,sans-serif;font-size:13px;font-weight:bold;">
                  ${nl.buttonLabel}
                </center>
              </v:roundrect>
              <![endif]-->
              <!--[if !mso]><!-->
              <a href="${nl.buttonMergeTag}"
                 style="display:inline-block;padding:10px 28px;background-color:${nl.buttonBg};color:${nl.buttonText};font-family:Calibri,Arial,sans-serif;font-size:13px;font-weight:700;text-decoration:none;border-radius:6px;">
                ${nl.buttonLabel}
              </a>
              <!--<![endif]-->
            </div>
          </mj-raw>`
      : "";

  // Logo image or text fallback — resolve relative paths to absolute URLs
  // so email clients can load them (emails can't use relative src).
  const logoUrl = nl.headerLogoUrl?.startsWith("/")
    ? `${window.location.origin}${nl.headerLogoUrl}`
    : nl.headerLogoUrl;
  const logoBlock = logoUrl
    ? `<mj-image src="${logoUrl}" alt="${tenant.companyName}"
                 width="280px" padding="0" />`
    : `<mj-text align="center" font-size="22px" font-weight="bold" color="${nl.headerText}"
               font-family="Georgia, 'Times New Roman', serif"
               letter-spacing="1px">
            ${tenant.companyName.toUpperCase()}
          </mj-text>`;

  return `
      <mj-section background-color="${nl.headerBg}" padding="32px 0 8px">
        <mj-column>
          ${logoBlock}
        </mj-column>
      </mj-section>
      <mj-section background-color="${nl.headerBg}" padding="0 0 16px">
        <mj-column>
          ${buttonMjml}
          <mj-divider border-color="${nl.dividerColor}" border-width="1px" padding="0 80px" />
        </mj-column>
      </mj-section>`;
}

function buildSocialElements(tenant) {
  const links = tenant.socialLinks;
  const elements = [];
  if (links.linkedin)
    elements.push(
      `<mj-social-element name="linkedin" href="${links.linkedin}" background-color="transparent" color="#E7E6E6" />`,
    );
  if (links.youtube)
    elements.push(
      `<mj-social-element name="youtube" href="${links.youtube}" background-color="transparent" color="#E7E6E6" />`,
    );
  if (links.x)
    elements.push(
      `<mj-social-element name="x" href="${links.x}" background-color="transparent" color="#E7E6E6" />`,
    );
  if (links.instagram)
    elements.push(
      `<mj-social-element name="instagram" href="${links.instagram}" background-color="transparent" color="#E7E6E6" />`,
    );
  if (links.web)
    elements.push(
      `<mj-social-element name="web" href="${links.web}" background-color="transparent" color="#E7E6E6" />`,
    );
  return elements.join("\n            ");
}

function buildFooterMjml(tenant) {
  const nl = tenant.newsletter;
  const socialElements = buildSocialElements(tenant);
  const socialBlock = socialElements
    ? `<mj-social font-size="12px" icon-size="24px" mode="horizontal" padding="0 0 16px"
                     icon-padding="0 8px"
                     inner-padding="0">
            ${socialElements}
          </mj-social>`
    : "";

  const addressBlock = tenant.address
    ? `<mj-text align="center" font-size="12px" color="${nl.footerText}" line-height="1.8" padding="0 20px 8px">
            ${tenant.address}
          </mj-text>`
    : "";

  return `
      <mj-section background-color="${nl.footerBg}" padding="24px 24px 60px">
        <mj-column>
          <mj-divider border-color="${nl.dividerColor}" border-width="1px" padding="0 0 20px" />
          ${socialBlock}
          ${addressBlock}
          <mj-text align="center" font-size="12px" color="${nl.footerText}" line-height="1.6" padding="0 20px 8px"
                   font-style="italic">
            Copyright &copy; ${new Date().getFullYear()} ${tenant.companyName}, All rights reserved.
          </mj-text>
          <mj-text align="center" font-size="12px" color="${nl.footerText}" line-height="1.6" padding="0 20px 8px">
            Contact: <a href="mailto:${tenant.contactEmail}" style="color:${nl.footerLink}">${tenant.contactEmail}</a>
          </mj-text>
          <mj-text align="center" font-size="12px" color="${nl.footerLink}" line-height="1.6" padding="8px 20px 16px">
            <a href="{{unsubscribe_link}}" style="color:${nl.footerLink}; text-decoration:underline">unsubscribe</a>
          </mj-text>
        </mj-column>
      </mj-section>
`;
}

function wrapMjml(headerMjml, footerMjml, bodyPadding = "32px 40px") {
  return `<mjml>
  ${MJ_HEAD}
    <mj-body background-color="#f4f4f4" width="600px">
      <mj-section padding="8px 0 0">
        <mj-column>
          <mj-text align="right" font-size="11px" color="#999999" padding="0 16px">
            <a href="{{web_version_link}}" style="color:#999999;text-decoration:underline;">View this email in browser</a>
          </mj-text>
        </mj-column>
      </mj-section>
      ${headerMjml}
      <mj-section background-color="#ffffff" padding="${bodyPadding}">
        <mj-column>
          <mj-text>{{BODY}}</mj-text>
        </mj-column>
      </mj-section>
      ${footerMjml}
    </mj-body>
  </mjml>`;
}

export function getWebcastInviteTemplate(tenant, event) {
  const headerMjml = buildHeaderMjml(tenant);
  const footerMjml = buildFooterMjml(tenant);
  const companyName = tenant.companyName;

  const eventDate = new Date(event.scheduledAt);
  const dateStr = eventDate.toLocaleDateString("en-GB", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const timeStr = eventDate.toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit",
  });
  const durationStr = event.durationMinutes ? ` (${event.durationMinutes} minutes)` : "";

  return {
    mjml: wrapMjml(headerMjml, footerMjml),
    subject: `You're Invited: ${event.title}`,
    defaultContent: `<p>Dear {{first_name}},</p>
<p>We would like to invite you to an upcoming ${companyName} webcast:</p>
<h2>${escapeHtml(event.title)}</h2>
${event.description ? `<p>${escapeHtml(event.description)}</p>` : ""}
<p><strong>Date:</strong> ${escapeHtml(dateStr)}<br />
<strong>Time:</strong> ${escapeHtml(timeStr)} UTC${escapeHtml(durationStr)}</p>
<p style="text-align:center;margin:24px 0;">
  <a href="{{registration_link}}" style="display:inline-block;padding:12px 32px;background:#213D61;color:#ffffff;text-decoration:none;font-weight:700;border-radius:6px;font-size:15px;">Register Now &rarr;</a>
</p>
<p>We look forward to welcoming you.</p>
<p>Best regards,<br /><strong>${companyName}</strong></p>`,
  };
}

export function getWebcastHighlightsTemplate(tenant, event, assets = {}) {
  const headerMjml = buildHeaderMjml(tenant);
  const footerMjml = buildFooterMjml(tenant);
  const companyName = tenant.companyName;

  const eventDate = new Date(event.scheduledAt);
  const dateStr = eventDate.toLocaleDateString("en-GB", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  // Build asset buttons — only include those that have URLs
  const buttons = [];
  if (assets.replayUrl) {
    buttons.push(`<a href="${escapeHtml(assets.replayUrl)}" style="display:inline-block;padding:10px 24px;background:#213D61;color:#ffffff;text-decoration:none;font-weight:700;border-radius:6px;font-size:14px;margin:4px;">Watch Replay &rarr;</a>`);
  }
  if (assets.slidesUrl) {
    buttons.push(`<a href="${escapeHtml(assets.slidesUrl)}" style="display:inline-block;padding:10px 24px;background:#213D61;color:#ffffff;text-decoration:none;font-weight:700;border-radius:6px;font-size:14px;margin:4px;">Download Slides</a>`);
  }
  if (assets.transcriptUrl) {
    buttons.push(`<a href="${escapeHtml(assets.transcriptUrl)}" style="display:inline-block;padding:10px 24px;background:#213D61;color:#ffffff;text-decoration:none;font-weight:700;border-radius:6px;font-size:14px;margin:4px;">Read Transcript</a>`);
  }
  const buttonRow = buttons.length
    ? `<p style="text-align:center;margin:24px 0;">${buttons.join("\n")}</p>`
    : "";

  // YouTube thumbnail block — clickable image
  const youtubeBlock = assets.youtubeUrl
    ? `<p style="text-align:center;margin:24px 0;">
  <a href="${escapeHtml(assets.youtubeUrl)}" target="_blank" style="text-decoration:none;">
    <img src="${escapeHtml(assets.thumbnailUrl || `https://img.youtube.com/vi/${extractYouTubeId(assets.youtubeUrl)}/hqdefault.jpg`)}" alt="Watch highlights" style="max-width:100%;border-radius:8px;border:1px solid #ddd;" />
  </a>
</p>`
    : "";

  const summaryBlock = assets.summary
    ? `${escapeHtml(assets.summary).replace(/\n/g, "<br />")}`
    : `[Add a summary of the webcast highlights here]`;

  return {
    mjml: wrapMjml(headerMjml, footerMjml),
    subject: `Webcast Highlights: ${event.title}`,
    defaultContent: `<p>Dear {{first_name}},</p>
<p>Thank you for your interest in our latest ${companyName} webcast. Here are the highlights from <strong>${escapeHtml(event.title)}</strong>, held on ${escapeHtml(dateStr)}.</p>
<h2>Key Takeaways</h2>
<p>${summaryBlock}</p>
${youtubeBlock}
${buttonRow}
<p>If you have any questions or would like to discuss the topics covered, please don't hesitate to reach out.</p>
<p>Best regards,<br /><strong>${companyName}</strong></p>`,
  };
}

function extractYouTubeId(url) {
  if (!url) return "";
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : "";
}

function escapeHtml(str) {
  if (!str) return "";
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function getTemplates(tenant) {
  const headerMjml = buildHeaderMjml(tenant);
  const footerMjml = buildFooterMjml(tenant);
  const companyName = tenant.companyName;

  return [
    {
      id: "blank",
      name: "Blank",
      subject: "",
      mjml: wrapMjml(headerMjml, footerMjml),
      defaultContent: "<p>Start writing your newsletter here...</p>",
    },
    {
      id: "weekly",
      name: "Weekly Digest",
      subject: `${tenant.id === "fgf" ? "FGF" : tenant.name} Weekly: {{first_name}}, Here's What's Moving Markets`,
      mjml: wrapMjml(headerMjml, footerMjml),
      defaultContent: `<p>Dear {{first_name}},</p>
<p>Welcome to this week's ${companyName} briefing. Here's what our team is watching across the geopolitical risk landscape.</p>
<h2>This Week's Key Developments</h2>
<h3>1. Supply Chain Realignment Accelerates</h3>
<p>New trade data reveals a significant shift in semiconductor supply routes, with implications for portfolio positioning across emerging markets. Our GRRI indicators flagged elevated risk in three key corridors.</p>
<h3>2. Sovereign Debt: Watch List Update</h3>
<p>Two nations have crossed critical thresholds on our Market Absorption Capacity framework. We've updated our risk matrices accordingly — details below.</p>
<h3>3. Policy Signals from the G7</h3>
<p>Ahead of next month's summit, early positioning suggests a coordinated stance on critical minerals. This has direct implications for ESG-weighted portfolios.</p>
<hr />
<h2>GRRI Dashboard Highlights</h2>
<p>Our Global Risk and Resilience Index moved <strong>+3.2 points</strong> this week, driven primarily by elevated readings in the Asia-Pacific corridor. Key movements:</p>
<ul>
<li><strong>Asia-Pacific Risk Index:</strong> 72.4 → 76.1 (Elevated)</li>
<li><strong>EMEA Stability Score:</strong> 68.9 → 67.2 (Marginal decline)</li>
<li><strong>Americas Policy Risk:</strong> 54.1 → 55.8 (Stable)</li>
</ul>
<hr />
<h2>Upcoming</h2>
<p><strong>Client Briefing — China Supply Chain Deep Dive</strong><br />Thursday 15:00 GMT | Exclusive for advisory clients</p>
<p>As always, reach out if you'd like a bespoke assessment for your portfolio.</p>
<p>Best regards,<br /><strong>Tina Fordham</strong><br />Founder, ${companyName}</p>`,
    },
    {
      id: "alert",
      name: "Market Alert",
      subject: `${tenant.id === "fgf" ? "FGF" : tenant.name} Alert: {{first_name}} — Urgent Geopolitical Development`,
      mjml: `<mjml>
  ${MJ_HEAD}
    <mj-body background-color="#f4f4f4" width="600px">
      ${headerMjml}
      <mj-section background-color="#ffffff" padding="24px 40px 8px">
        <mj-column>
          <mj-text padding="16px 20px" background-color="#163E64" border-radius="8px"
                   font-size="11px" color="#E7E6E6" line-height="1.5"
                   font-family="Calibri, Arial, Helvetica, sans-serif">
            <strong style="font-size:16px;display:block;margin-bottom:6px">GEOPOLITICAL RISK ALERT</strong>
            Issued: ${new Date().toLocaleDateString("en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </mj-text>
        </mj-column>
      </mj-section>
      <mj-section background-color="#ffffff" padding="16px 40px 32px">
        <mj-column>
          <mj-text>{{BODY}}</mj-text>
        </mj-column>
      </mj-section>
      ${footerMjml}
    </mj-body>
  </mjml>`,
      defaultContent: `<p>Dear {{first_name}},</p>
<p>We are issuing an elevated risk alert following developments that may have material implications for your portfolio positioning.</p>
<h2>Situation Summary</h2>
<p>[Describe the geopolitical event and its immediate context here]</p>
<h2>Market Transmission Assessment</h2>
<p>Our MAC Framework analysis indicates the following transmission channels are active:</p>
<ul>
<li><strong>Liquidity Pillar:</strong> [Assessment]</li>
<li><strong>Volatility Pillar:</strong> [Assessment]</li>
<li><strong>Policy Pillar:</strong> [Assessment]</li>
</ul>
<h2>Recommended Actions</h2>
<p>[Specific guidance based on the analysis]</p>
<p>We will issue a follow-up assessment within 48 hours as the situation develops. Advisory clients can schedule a call with our team for bespoke portfolio guidance.</p>
<p><strong>Tina Fordham</strong><br />${companyName}</p>`,
    },
    {
      id: "client",
      name: "Client Update",
      subject: `${tenant.id === "fgf" ? "FGF" : tenant.name} Client Update: Your Monthly Risk Briefing`,
      mjml: wrapMjml(headerMjml, footerMjml),
      defaultContent: `<p>Dear {{first_name}},</p>
<p>Please find below your monthly risk briefing from ${companyName}, tailored to your portfolio exposure and areas of interest.</p>
<h2>Executive Summary</h2>
<p>The global risk landscape has [improved/deteriorated/remained stable] this month. Key themes include [topic 1], [topic 2], and [topic 3].</p>
<h2>Your Watchlist</h2>
<p>Based on your portfolio exposure to {{company}}'s key markets:</p>
<ul>
<li><strong>[Region/Sector 1]:</strong> [Brief assessment]</li>
<li><strong>[Region/Sector 2]:</strong> [Brief assessment]</li>
<li><strong>[Region/Sector 3]:</strong> [Brief assessment]</li>
</ul>
<h2>Action Items</h2>
<p>We recommend reviewing the following positions in light of this month's developments:</p>
<ol>
<li>[Recommendation 1]</li>
<li>[Recommendation 2]</li>
<li>[Recommendation 3]</li>
</ol>
<p>Your next quarterly strategy call is scheduled for [date]. Please let us know if you'd like to discuss any of these points ahead of that meeting.</p>
<p>Warm regards,<br /><strong>Tina Fordham</strong><br />Founder, ${companyName}</p>`,
    },
  ];
}
